import React, { useState, useEffect, useRef } from 'react';
import AdminUserList from './AdminUserList';
import AdminFeatureGroups from './AdminFeatureGroups';
import AIPrompts from './AIPrompts';
import { getSystemSetting, setSystemSetting, getUserFeatureGroups } from '../services/featureService';
import { logger } from '../utils/logger';
import './SystemAdmin.css';

const SystemAdmin = ({ isSuperuser }) => {
  const [activePanel, setActivePanel] = useState('Users');
  const [footerFeatureGroupId, setFooterFeatureGroupId] = useState(null);
  const [featureGroups, setFeatureGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const aiPromptsRef = useRef(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!isSuperuser) return;
      try {
        setLoading(true);
        const [setting, groups] = await Promise.all([
          getSystemSetting('footer_feature_group_id'),
          getUserFeatureGroups()
        ]);
        setFooterFeatureGroupId(setting || '');
        setFeatureGroups(groups);
      } catch (err) {
        logger.error('Error loading system settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [isSuperuser]);

  const handleFooterFeatureGroupChange = async (e) => {
    const newValue = e.target.value || null;
    setFooterFeatureGroupId(newValue);
    
    try {
      setSaving(true);
      await setSystemSetting('footer_feature_group_id', newValue);
      // Show success feedback
      const select = e.target;
      const originalBg = select.style.backgroundColor;
      select.style.backgroundColor = '#e8f5e9';
      setTimeout(() => {
        select.style.backgroundColor = originalBg;
      }, 1000);
    } catch (err) {
      logger.error('Error saving footer feature group setting:', err);
      alert('Failed to save setting. Please try again.');
      // Revert on error
      const currentValue = await getSystemSetting('footer_feature_group_id');
      setFooterFeatureGroupId(currentValue || '');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperuser) {
    return (
      <div className="system-admin-access">
        <p>You must be a superuser to access System Admin.</p>
      </div>
    );
  }

  return (
    <div className="system-admin">
      <div className="system-admin-header">
        <h2>System Administration</h2>
        <p className="system-admin-directions">
          The following tools are available to the superuser of the system.
        </p>
        
        <div className="footer-feature-group-selector">
          <label htmlFor="footer-feature-group-select">
            Use this Feature Map for Footer Release Version:
          </label>
          <select
            id="footer-feature-group-select"
            value={footerFeatureGroupId || ''}
            onChange={handleFooterFeatureGroupChange}
            disabled={loading || saving}
            className="footer-feature-group-select"
          >
            <option value="">None (use default)</option>
            {featureGroups.map(group => (
              <option key={group.id} value={group.id}>
                {group.display_name}
              </option>
            ))}
          </select>
          {saving && <span className="saving-indicator">Saving...</span>}
        </div>

        <div className="system-admin-nav-pills">
          <button
            className={`nav-pill ${activePanel === 'Users' ? 'active' : ''}`}
            onClick={() => {
              if (activePanel === 'AI Data' && aiPromptsRef.current?.hasUnsavedChanges()) {
                if (!window.confirm('You have unsaved changes in the System AI Prompt form. Are you sure you want to switch panels? Your changes will be lost.')) {
                  return;
                }
              }
              setActivePanel('Users');
            }}
          >
            Users
          </button>
          <button
            className={`nav-pill ${activePanel === 'Feature Maps' ? 'active' : ''}`}
            onClick={() => {
              if (activePanel === 'AI Data' && aiPromptsRef.current?.hasUnsavedChanges()) {
                if (!window.confirm('You have unsaved changes in the System AI Prompt form. Are you sure you want to switch panels? Your changes will be lost.')) {
                  return;
                }
              }
              setActivePanel('Feature Maps');
            }}
          >
            Feature Maps
          </button>
          <button
            className={`nav-pill ${activePanel === 'AI Data' ? 'active' : ''}`}
            onClick={() => {
              if (activePanel === 'AI Data' && aiPromptsRef.current?.hasUnsavedChanges()) {
                if (!window.confirm('You have unsaved changes in the System AI Prompt form. Are you sure you want to switch panels? Your changes will be lost.')) {
                  return;
                }
              }
              setActivePanel('AI Data');
            }}
          >
            AI Data
          </button>
        </div>
      </div>
      <div className="system-admin-content">
        {activePanel === 'Users' && (
          <AdminUserList />
        )}
        {activePanel === 'Feature Maps' && (
          <AdminFeatureGroups />
        )}
        {activePanel === 'AI Data' && (
          <AIPrompts ref={aiPromptsRef} />
        )}
      </div>
    </div>
  );
};

export default SystemAdmin;

